import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { calculatePopoverPosition } from '../components/Panel/lib/panelPosition';

export const usePopoverPosition = ({ isFixed, position, rerender, isOpen, onClose }) => {
  const [panelFixedPosition, setPanelFixedPosition] = useState({});
  const [isPositioned, setIsPositioned] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelWidth = panelRef?.current?.getBoundingClientRect()?.width || 140;

  // Position calculation
  const updatePopoverPosition = useCallback(() => {
    if (!isFixed || !buttonRef.current || !panelRef?.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    // offsetWidth/offsetHeight ignore transforms, so the entrance animation's scale
    // doesn't skew the measurement when the position is recomputed mid-animation.
    const panelRect = {
      width: panelRef.current.offsetWidth,
      height: panelRef.current.offsetHeight
    };
    const actualPanelWidth = panelRect.width || panelWidth;
    const newPosition = calculatePopoverPosition({
      buttonRect,
      panelRect,
      panelWidth: actualPanelWidth,
      position
    });

    setPanelFixedPosition(newPosition);
    setIsPositioned(true);
  }, [isFixed, position, panelWidth, rerender]);

  // Reset positioning state when popover closes
  useLayoutEffect(() => {
    if (!isOpen) {
      setIsPositioned(false);
    }
  }, [isOpen]);

  // Layout effects for positioning
  useLayoutEffect(() => {
    if (isOpen) {
      updatePopoverPosition();
    }
  }, [isOpen, updatePopoverPosition, rerender]);

  useLayoutEffect(() => {
    if (isFixed && panelRef?.current) {
      const handleResize = () => {
        if (panelRef.current?.isConnected) onClose();
      };

      const handleScroll = (event) => {
        if (
          (panelRef?.current &&
            event?.target instanceof Node &&
            panelRef?.current?.contains(event.target)) ||
          event?.target?.classList?.contains('select__menu-list')
        )
          return;
        updatePopoverPosition();
      };

      window.addEventListener('resize', handleResize, true);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize, true);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [updatePopoverPosition, isFixed, onClose]);

  // Handle panel ref callback to ensure position is calculated when panel is first rendered
  const handlePanelRef = useCallback(
    (node) => {
      if (node && isFixed) {
        panelRef.current = node;
        // Calculate position immediately and then again in the next frame for accuracy
        updatePopoverPosition();
        requestAnimationFrame(() => {
          updatePopoverPosition();
        });
      } else if (!isFixed) {
        panelRef.current = node;
        setIsPositioned(true);
      }
    },
    [isFixed, updatePopoverPosition]
  );

  return {
    panelFixedPosition,
    buttonRef,
    panelRef,
    handlePanelRef,
    updatePopoverPosition,
    isPositioned
  };
};
